import React from 'react'
// components
import { Button, BUTTON_LAYOUTS, Modal } from 'components/UI'
// hooks
import { useModalData } from 'hooks/index'
// constants
import { Modals } from 'constants/modals'
// types
import { TMatrix } from 'components/Modals/MatrixModal/components/Matrix/types'
// views
import {
  Header,
  Container,
  Selected,
  RowHeader,
  Description,
  RowContainer,
  AppetiteText,
  AppetiteColor,
  AppetitesWrapper,
  ButtonsContainer
} from './views'
// utils
import { getTextFromHtml } from 'utils/html'
import { filter, find, flatten, pipe } from 'lodash/fp'

interface Props {
  label: string
  matrix: TMatrix
}

const AppetiteConfirmationModal: React.FC<Props> = ({ label, matrix }) => {
  const {
    hideModal,
    modalData: { isOpen = false, onConfirm, onCancel, optionId }
  } = useModalData({ modalType: Modals.RISK_MATRIX_APPETITE_CONF_MODAL })

  if (!isOpen) {
    return null
  }

  const handleConfirmClick = () => {
    onConfirm()
    hideModal()
  }

  const handleCancelClick = () => {
    onCancel()
    hideModal()
  }

  const getSections = () => {
    const sections = []

    matrix.topSection.forEach((row, idx) => {
      if (row.some((item) => item.selected)) {
        sections.push({
          label: getTextFromHtml(row[0].value),
          appetites: matrix.appetites[idx]?.options.map((option) => {
            const matrixOption = matrix.options.find((item) => item.id === option.matrixOptionId)

            return { ...option, label: `${matrixOption.label} (${parseFloat(matrixOption.value)})` }
          })
        })
      }
    })

    return sections.filter((item) => item.appetites.length)
  }

  const sections = getSections()

  const selected = pipe(
    filter((rows: any) => rows.some(({ id }) => id === optionId)),
    flatten,
    find(({ id }) => id === optionId),
    (res: any) =>
      matrix.options.find(({ id }) => (typeof res?.value === 'string' ? id === res?.value : id === res?.value.id))
  )(matrix.bRightSection)

  return (
    <Modal isOpen={isOpen} width='md' hideModal={hideModal}>
      <Header>{label}</Header>
      <Container>
        <Selected color={selected.color}>
          <b>Selected:</b> {`${selected.label} (${selected.value})`}
        </Selected>
        <Description>
          Please review the Appetite Statement below, which has been triggered by your selection, and use it to confirm
          the chosen risk level
        </Description>
        {sections.map((item, idx) => (
          <RowContainer key={idx}>
            <RowHeader>{item.label}</RowHeader>
            <AppetitesWrapper>
              <div>
                {item.appetites.map((option) => (
                  <AppetiteColor key={option.label} color={option.color}>
                    {option.label}
                  </AppetiteColor>
                ))}
              </div>
              <div>
                {item.appetites.map((option) => (
                  <AppetiteText key={option.message}> {option.message}</AppetiteText>
                ))}
              </div>
            </AppetitesWrapper>
          </RowContainer>
        ))}
      </Container>
      <ButtonsContainer>
        <Button shadowLess type='button' onClick={handleConfirmClick} layout={BUTTON_LAYOUTS.PURPLE}>
          Confirm
        </Button>
        <Button shadowLess onClick={handleCancelClick} layout={BUTTON_LAYOUTS.PURPLE_INVERTED}>
          Cancel
        </Button>
      </ButtonsContainer>
    </Modal>
  )
}

export default AppetiteConfirmationModal
